import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, CheckCircle2, Loader2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button.jsx';
import OrnateDivider from '../components/OrnateDivider';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const [state, setState] = useState({ loading: true, valid: false, email: '', name: '' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setState({ loading: false, valid: false, email: '', name: '' });
        return;
      }
      try {
        const r = await axios.get(`${API}/auth/setup-token/${token}`);
        setState({ loading: false, valid: true, email: r.data.email, name: r.data.name });
      } catch (err) {
        setState({
          loading: false,
          valid: false,
          email: '',
          name: '',
          error: err?.response?.data?.detail || 'Link is invalid or expired',
        });
      }
    };
    run();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/auth/set-password`, { token, new_password: password });
      localStorage.setItem('token', r.data.access_token);
      toast.success('Password set — welcome!');
      // Hard nav so AuthContext re-reads the token
      window.location.href = '/courses/1';
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not set password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen mystical-bg pt-28 pb-20 relative">
      <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-8">
            <OrnateDivider label="Welcome" variant="lotus" className="mb-4 max-w-md mx-auto" />
            <h1 className="text-3xl md:text-4xl font-heading font-bold golden-text text-glow uppercase tracking-wider">
              Set your password
            </h1>
          </div>

          {state.loading && (
            <div className="card-mystical rounded-2xl p-10 text-center">
              <Loader2 className="mx-auto text-primary animate-spin" size={28} />
              <p className="mt-3 text-primary/70">Checking your link…</p>
            </div>
          )}

          {!state.loading && !state.valid && (
            <div className="card-mystical rounded-2xl p-8 text-center" data-testid="setup-invalid">
              <p className="text-primary/85 mb-4">
                {state.error || 'This link is invalid or has expired.'}
              </p>
              <p className="text-sm text-primary/60 mb-6">
                Please contact <strong>Siddha Samaj</strong> to get a new setup link sent to your
                email.
              </p>
              <Button onClick={() => navigate('/')} className="ornate-button px-8">
                Back to home
              </Button>
            </div>
          )}

          {!state.loading && state.valid && (
            <form
              onSubmit={submit}
              className="card-mystical rounded-2xl p-8 space-y-5"
              data-testid="setup-form"
            >
              <div>
                <p className="text-primary/85 leading-relaxed">
                  Namaste <strong>{state.name || 'friend'}</strong>, choose a password for your
                  account <code className="text-primary/90">{state.email}</code>.
                </p>
              </div>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.3em] text-primary/60 block mb-1">
                  New password <span className="text-primary/45">(min 8 characters)</span>
                </span>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-primary/5 border border-primary/25 rounded-md px-3 py-2 pr-10 text-sm text-primary focus:outline-none focus:border-primary/60"
                    required
                    minLength={8}
                    data-testid="setup-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-primary/60 hover:text-primary transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    data-testid="setup-password-toggle"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.3em] text-primary/60 block mb-1">
                  Confirm password
                </span>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full bg-primary/5 border border-primary/25 rounded-md px-3 py-2 pr-10 text-sm text-primary focus:outline-none focus:border-primary/60"
                    required
                    minLength={8}
                    data-testid="setup-confirm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-primary/60 hover:text-primary transition-colors"
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                    data-testid="setup-confirm-toggle"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </label>
              <Button
                type="submit"
                disabled={submitting}
                className="ornate-button w-full"
                data-testid="setup-submit"
              >
                {submitting ? (
                  <><Loader2 className="animate-spin" size={14} /> Setting password…</>
                ) : (
                  <><KeyRound size={14} /> Set password & begin</>
                )}
              </Button>
              <p className="text-xs text-primary/55 flex items-center gap-2">
                <CheckCircle2 size={12} /> After this, you will land straight on Grahita Shakti.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default SetPassword;
